import { Email, ExpandMore, Phone } from "@mui/icons-material";
import { Box, Button, Collapse, Link, Stack, Typography, useTheme } from "@mui/material";
import { useState } from "react";

import { config } from "@/config.ts";
import { clearAllSiteData } from "@/store/boot";
import { SUPPORT_EMAIL, SUPPORT_PHONE_DISPLAY, SUPPORT_PHONE_TEL } from "@/utils/support.ts";

type StorageErrorProps = {
  reason: string;
};

type BusyAction = "reload" | "logout" | null;

/**
 * Shown when the app can't initialize its local IndexedDB-backed database and the one-time
 * automatic recovery (see `storageRecovery.ts`) has already failed. Deliberately minimal: a
 * heading, the two recovery actions the legacy debug menu exposed ("Clear Data and Reload" /
 * "Clear Data and Logout"), prominent support contact details, and the technical reason tucked
 * behind a collapsed disclosure.
 */
export default function StorageError({ reason }: StorageErrorProps) {
  const theme = useTheme();
  const [busy, setBusy] = useState<BusyAction>(null);
  const [showDetails, setShowDetails] = useState(false);

  const handleClearAndReload = async () => {
    setBusy("reload");
    try {
      await clearAllSiteData();
    } finally {
      window.location.assign("/");
    }
  };

  const handleClearAndLogout = async () => {
    setBusy("logout");
    try {
      await clearAllSiteData({ clearCookies: true });
    } finally {
      window.location.assign(`${config.baseDomain}/auth/logout`);
    }
  };

  const contactLinkSx = {
    display: "inline-flex",
    alignItems: "center",
    gap: 1,
    fontSize: "1.125rem",
    fontWeight: 600,
  };

  return (
    <Stack
      role="alert"
      sx={{
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        minHeight: "100dvh",
        px: 3,
        py: 6,
      }}
    >
      <Box
        sx={{
          maxWidth: 460,
          width: "100%",
          p: 4,
          borderRadius: 3,
          backgroundColor: theme.palette.brand.other.black.main,
          border: `1px solid ${theme.palette.secondary.main}`,
          boxShadow: theme.shadows[8],
        }}
      >
        <Typography variant="h5" component="h1" sx={{ mb: 4, fontWeight: 700 }}>
          Something went wrong
        </Typography>

        <Stack spacing={1.5}>
          <Button variant="contained" color="primary" onClick={handleClearAndReload} disabled={busy !== null}>
            {busy === "reload" ? "Clearing…" : "Clear Data and Reload"}
          </Button>
          <Button variant="outlined" color="primary" onClick={handleClearAndLogout} disabled={busy !== null}>
            {busy === "logout" ? "Clearing…" : "Clear Data and Logout"}
          </Button>
        </Stack>

        <Stack spacing={1.5} sx={{ mt: 4, alignItems: "center" }}>
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
            Still stuck? Contact support
          </Typography>
          <Link href={`tel:${SUPPORT_PHONE_TEL}`} sx={contactLinkSx}>
            <Phone fontSize="small" />
            {SUPPORT_PHONE_DISPLAY}
          </Link>
          <Link href={`mailto:${SUPPORT_EMAIL}`} sx={contactLinkSx}>
            <Email fontSize="small" />
            {SUPPORT_EMAIL}
          </Link>
        </Stack>

        <Box sx={{ mt: 4, pt: 2, borderTop: `1px solid ${theme.palette.secondary.main}` }}>
          <Button
            variant="text"
            onClick={() => setShowDetails((open) => !open)}
            aria-expanded={showDetails}
            sx={{
              width: "auto",
              p: 0.5,
              fontWeight: 500,
              textTransform: "none",
              color: theme.palette.text.secondary,
              "&:hover": { backgroundColor: "transparent" },
            }}
            endIcon={
              <ExpandMore
                fontSize="small"
                sx={{ transition: "transform 150ms ease", transform: showDetails ? "rotate(180deg)" : "none" }}
              />
            }
          >
            {showDetails ? "Hide details" : "Show details"}
          </Button>
          <Collapse in={showDetails}>
            <Box
              sx={{
                mt: 1.5,
                px: 1.5,
                py: 1,
                borderRadius: 1,
                textAlign: "left",
                backgroundColor: theme.palette.primary.dark,
              }}
            >
              <Typography
                variant="caption"
                component="pre"
                sx={{
                  m: 0,
                  fontFamily: "monospace",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                  color: theme.palette.text.disabled,
                }}
              >
                {reason}
              </Typography>
            </Box>
          </Collapse>
        </Box>
      </Box>
    </Stack>
  );
}
